// src/components/Dashboard/Igloos.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { db, auth, API_BASE_URL } from "../../firebase/firebaseConfig";
import {
  collection,
  getDocs,
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  deleteField,
  onSnapshot,
} from "firebase/firestore";
import IglooMembers from "./IglooMembers";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Separator } from "../ui/separator";
import { Home, ChevronDown, Plus, Trash2, Users, Check, X, Zap } from "lucide-react";
import ErrorModal from "../ui/ErrorModal";

function Igloos({ familyID }) {
  const [igloos, setIgloos] = useState([]);
  const [activeIgloos, setActiveIgloos] = useState(new Set());
  const [editingIglooID, setEditingIglooID] = useState(null);
  const [iglooName, setIglooName] = useState("");
  const [newIglooName, setNewIglooName] = useState("");

  const [invites, setInvites] = useState([]);
  const [familyNames, setFamilyNames] = useState({});

  const [reloadTrigger, setReloadTrigger] = useState(0);

  const [creatingIgloo, setCreatingIgloo] = useState(false);
  const [removingIgloos, setRemovingIgloos] = useState(new Set());

  const [expandedSections, setExpandedSections] = useState({
    inactive: false,
  });
  const [configuringIgloos, setConfiguringIgloos] = useState({});
  const [memberCounts, setMemberCounts] = useState({});
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [resetEditOnClose, setResetEditOnClose] = useState(false);

  // Auto mode state - when enabled, only Centre position is available
  const [autoMode, setAutoMode] = useState(false);

  // Live igloo data managed here to avoid child snapshot listeners
  const [iglooLiveData, setIglooLiveData] = useState({}); // { [iglooID]: { members: [], isController, loading, controllersLoading, pendingSet, docData } }
  const iglooDocUnsubsRef = useRef(new Map());
  const iglooPendingUnsubsRef = useRef(new Map());
  const [subscriptionReloadSeed, setSubscriptionReloadSeed] = useState(0);


  const unsubscribeIglooListeners = (iglooID) => {
    const u1 = iglooDocUnsubsRef.current.get(iglooID);
    if (u1) {
      try { u1(); } catch (_) {}
      iglooDocUnsubsRef.current.delete(iglooID);
    }
    const u2 = iglooPendingUnsubsRef.current.get(iglooID);
    if (u2) {
      try { u2(); } catch (_) {}
      iglooPendingUnsubsRef.current.delete(iglooID);
    }
  };
  const resubscribeAllEnabled = () => setSubscriptionReloadSeed((s) => s + 1);

  // Compute which igloos need live subscriptions (expanded configs + invites preview)
  const enabledSubscriptionIglooIDs = useMemo(() => {
    const enabled = new Set();
    Object.entries(configuringIgloos).forEach(([key, value]) => {
      if (value) {
        const parts = key.split("-");
        const iglooId = parts[parts.length - 1];
        if (iglooId) enabled.add(iglooId);
      }
    });
    invites.forEach((inv) => enabled.add(inv.iglooID));
    return enabled;
  }, [configuringIgloos, invites]);

  // Attach/detach subscriptions based on enabled set
  useEffect(() => {
    const attachForIgloo = (iglooID) => {
      if (!iglooDocUnsubsRef.current.has(iglooID)) {
        setIglooLiveData((prev) => ({
          ...prev,
          [iglooID]: {
            members: [],
            isController: false,
            loading: true,
            controllersLoading: true,
            pendingSet: new Set(),
            docData: { controllers: [], members: [] },
          },
        }));

        const iglooDocRef = doc(db, "iglooInfo", iglooID);
        const unsubDoc = onSnapshot(
          iglooDocRef,
          (docSnap) => {
            if (!docSnap.exists()) {
              setIglooLiveData((prev) => ({
                ...prev,
                [iglooID]: {
                  ...(prev[iglooID] || {}),
                  members: [],
                  isController: false,
                  loading: false,
                  controllersLoading: false,
                  docData: { controllers: [], members: [] },
                },
              }));
              return;
            }
            const data = docSnap.data();
            const controllers = data.controllers || [];
            const membersArr = data.members || [];

            setIglooLiveData((prev) => {
              const pendingSet = prev[iglooID]?.pendingSet || new Set();
              const mergedMembers = [
                ...controllers.map((fid) => ({ familyID: fid, role: "controller", isPending: pendingSet.has(fid) })),
                ...membersArr.map((fid) => ({ familyID: fid, role: "member", isPending: pendingSet.has(fid) })),
              ];
              return {
                ...prev,
                [iglooID]: {
                  ...(prev[iglooID] || {}),
                  members: mergedMembers,
                  isController: controllers.includes(familyID),
                  loading: false,
                  controllersLoading: false,
                  docData: { controllers, members: membersArr },
                },
              };
            });
          },
          (error) => {
            if (error?.code === "permission-denied" || `${error?.message}`.includes("Missing or insufficient permissions")) {
              setIglooLiveData((prev) => ({
                ...prev,
                [iglooID]: {
                  ...(prev[iglooID] || {}),
                  loading: false,
                  controllersLoading: false,
                },
              }));
              return;
            }
            console.error(`Igloo ${iglooID} doc listener error:`, error);
          }
        );
        iglooDocUnsubsRef.current.set(iglooID, unsubDoc);
      }

      if (!iglooPendingUnsubsRef.current.has(iglooID)) {
        const pendingRef = collection(db, "iglooInfo", iglooID, "pending");
        const unsubPending = onSnapshot(
          pendingRef,
          (colSnap) => {
            const pendingSet = new Set();
            colSnap.forEach((d) => pendingSet.add(d.id));
            setIglooLiveData((prev) => {
              const current = prev[iglooID] || { docData: { controllers: [], members: [] } };
              const controllers = current.docData?.controllers || [];
              const membersArr = current.docData?.members || [];
              const mergedMembers = [
                ...controllers.map((fid) => ({ familyID: fid, role: "controller", isPending: pendingSet.has(fid) })),
                ...membersArr.map((fid) => ({ familyID: fid, role: "member", isPending: pendingSet.has(fid) })),
              ];
              return {
                ...prev,
                [iglooID]: {
                  ...current,
                  members: mergedMembers,
                  pendingSet,
                },
              };
            });
          },
          (error) => {
            if (error?.code === "permission-denied" || `${error?.message}`.includes("Missing or insufficient permissions")) {
              return;
            }
            console.error(`Igloo ${iglooID} pending listener error:`, error);
          }
        );
        iglooPendingUnsubsRef.current.set(iglooID, unsubPending);
      }
    };

    const unsubForIgloo = (iglooID) => {
      Promise.resolve().then(() => {
        const u1 = iglooDocUnsubsRef.current.get(iglooID);
        if (u1) {
          try { u1(); } catch (_) {}
          iglooDocUnsubsRef.current.delete(iglooID);
        }
        const u2 = iglooPendingUnsubsRef.current.get(iglooID);
        if (u2) {
          try { u2(); } catch (_) {}
          iglooPendingUnsubsRef.current.delete(iglooID);
        }
      });
    };

    // Attach for enabled IDs
    enabledSubscriptionIglooIDs.forEach((iglooID) => attachForIgloo(iglooID));

    // Detach for disabled IDs
    const allSubscribed = new Set([
      ...Array.from(iglooDocUnsubsRef.current.keys()),
      ...Array.from(iglooPendingUnsubsRef.current.keys()),
    ]);
    allSubscribed.forEach((iglooID) => {
      if (!enabledSubscriptionIglooIDs.has(iglooID)) {
        unsubForIgloo(iglooID);
      }
    });

    // Cleanup on unmount
    return () => {
      Array.from(iglooDocUnsubsRef.current.values()).forEach((u) => u());
      Array.from(iglooPendingUnsubsRef.current.values()).forEach((u) => u());
      iglooDocUnsubsRef.current.clear();
      iglooPendingUnsubsRef.current.clear();
    };
  }, [db, familyID, enabledSubscriptionIglooIDs, subscriptionReloadSeed]);

  const getIglooLiveProps = (iglooID) => {
    const entry = iglooLiveData[iglooID];
    return {
      members: entry?.members || [],
      isController: entry?.isController || false,
      loading: entry?.loading ?? true,
      controllersLoading: entry?.controllersLoading ?? false,
    };
  };

  const toggleIglooConfig = (iglooId) => {
    setConfiguringIgloos((prev) => ({
      ...prev,
      [iglooId]: !prev[iglooId],
    }));
  };

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  useEffect(() => {
    loadIgloos();
    loadActiveIgloos();
    loadPendingIglooInvites();
    loadAutoMode();
  }, [familyID, reloadTrigger]);

  // Load auto mode status from familyInfo
  const loadAutoMode = async () => {
    try {
      const familyDoc = await getDoc(doc(db, "familyInfo", familyID));
      if (familyDoc.exists()) {
        setAutoMode(familyDoc.data().autoMode || false);
      }
    } catch (error) {
      console.error("Error loading auto mode status:", error);
    }
  };

  useEffect(() => {
    if (igloos.length > 0) {
      loadMemberCounts();
    }
  }, [igloos]);

  // Load igloos associated with the family
  const loadIgloos = async () => {
    const igloosList = [];
    const igloosCollectionRef = collection(db, "familyInfo", familyID, "igloos");
    try {
      const snapshot = await getDocs(igloosCollectionRef);
      for (let docSnap of snapshot.docs) {
        const iglooID = docSnap.id;
        const iglooData = docSnap.data();
        const iglooName = iglooData.name;
        const position = iglooData.position || "Inactive";
        const isController = await checkIfController(iglooID);
        igloosList.push({ iglooID, iglooName, position, isController });
      }
      setIgloos(igloosList);
    } catch (error) {
      console.error("Error loading igloos:", error);
    }
  };

  const checkIfController = async (iglooID) => {
    const iglooRef = doc(db, "iglooInfo", iglooID);
    try {
      const docSnap = await getDoc(iglooRef);
      if (docSnap.exists()) {
        const controllers = docSnap.data().controllers || [];
        return controllers.includes(familyID);
      }
      return false;
    } catch (error) {
      console.error("Error checking controller status:", error);
      return false;
    }
  };

  const loadActiveIgloos = async () => {
    const activeIgloosSet = new Set();
    const activeIgloosCollectionRef = collection(
      db,
      "familyInfo",
      familyID,
      "activeIgloos"
    );
    try {
      const snapshot = await getDocs(activeIgloosCollectionRef);
      snapshot.docs.forEach((docSnap) => {
        activeIgloosSet.add(docSnap.id);
      });
      setActiveIgloos(activeIgloosSet);
    } catch (error) {
      console.error("Error loading active igloos:", error);
    }
  };

  const toggleIglooStatus = async (iglooID) => {
    const isCurrentlyActive = activeIgloos.has(iglooID);
    const activeIgloosCollectionRef = collection(
      db,
      "familyInfo",
      familyID,
      "activeIgloos"
    );

    if (!isCurrentlyActive && activeIgloos.size >= 3) {
      setErrorMessage("You're already at the limit of 3 active Higlos. Please deactivate one before activating another.");
      setShowErrorModal(true);
      return;
    }

    try {
      if (isCurrentlyActive) {
        await deleteDoc(doc(activeIgloosCollectionRef, iglooID));
        activeIgloos.delete(iglooID);
      } else {
        await setDoc(doc(activeIgloosCollectionRef, iglooID), {});
        activeIgloos.add(iglooID);
      }
      setActiveIgloos(new Set(activeIgloos)); // Update state with a new Set to trigger re-render
    } catch (error) {
      console.error("Error toggling igloo status:", error);
    }
  };

  // Remove family from an igloo
  const removeFamilyFromIgloo = async (iglooID) => {
    const iglooRef = doc(db, "iglooInfo", iglooID);
    try {
      const docSnap = await getDoc(iglooRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const updatedControllers = (data.controllers || []).filter(id => id !== familyID);
        const updatedMembers = (data.members || []).filter(id => id !== familyID);

        await updateDoc(iglooRef, {
          controllers: updatedControllers,
          members: updatedMembers
        });
        loadIgloos();
      }
    } catch (error) {
      console.error("Error removing family from igloo:", error);
    }
  };

  // Create a new igloo
  const createNewIgloo = async () => {
    if (!newIglooName || !newIglooName.trim()) {
      setErrorMessage("The new Higlo name cannot be blank.");
      setShowErrorModal(true);
      return;
    }

    setCreatingIgloo(true);

    const currentUser = auth.currentUser;
    const idToken = await currentUser.getIdToken();

    try {
      const response = await fetch(`${API_BASE_URL}/user/createIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ familyID, newIglooName }),
      });
      const data = await response.json();
      if (data.success) {
        setNewIglooName("");
      } else {
        setErrorMessage('Create Higlo failed, please try again.');
        setShowErrorModal(true);
      }
      loadIgloos();
    } catch (error) {
    console.error("Error creating new igloo:", error);
      setErrorMessage('Create Higlo failed, please try again.');
      setShowErrorModal(true);
    } finally {
      setCreatingIgloo(false);
    }
  };

  const handleEditToggle = (iglooID, currentName) => {
    setEditingIglooID(iglooID);
    setIglooName(currentName);
  };

  const handleSave = async (iglooID) => {
    const iglooRef = doc(db, "familyInfo", familyID, "igloos", iglooID);
    const newIglooName = (iglooName || "").trim();
    if (!newIglooName) {
      setErrorMessage("The Higlo name cannot be empty.");
      setShowErrorModal(true);
      setResetEditOnClose(true);
      return;
    }
    try {
      await updateDoc(iglooRef, { name: newIglooName });
      setEditingIglooID(null);
      loadIgloos();
    } catch (error) {
      console.error("Error updating igloo name:", error);
    }
  };

  const handleCancel = () => {
    setEditingIglooID(null);
  };

  const handleChange = (evt) => {
    setIglooName(evt.target.value);
  };

  const handleRemoveIgloo = async (iglooID) => {
    const igloo = igloos.find((igloo) => igloo.iglooID === iglooID);
    if (igloo.position !== "Inactive") {
      setErrorMessage("Only inactive Higlos can be removed.");
      setShowErrorModal(true);
      return;
    }

    setRemovingIgloos(prev => new Set([...prev, iglooID]));

    try {
      const iglooRef = doc(db, "iglooInfo", iglooID);
      const docSnap = await getDoc(iglooRef);
      if (!docSnap.exists()) {
        return;
      }

      const docData = docSnap.data();
      const otherMembers = (docData.members || []).filter(id => id !== familyID);

      if (otherMembers.length > 0) {
        setErrorMessage("You can only remove a Higlo if there are no members apart from you.");
        setShowErrorModal(true);
        return;
      }
        
      const currentUser = auth.currentUser;
      const idToken = await currentUser.getIdToken();

      const response = await fetch(`${API_BASE_URL}/user/deleteIgloo`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`
        },
        body: JSON.stringify({ familyID, iglooID }),
      });

      const data = await response.json();
      if (!data.success) {
        setErrorMessage("Higlo deletion failed. Please try again.");
        setShowErrorModal(true);
      }
      loadIgloos();
    } catch (error) {
      console.error('Error:', error);
      setErrorMessage('An error occurred. Please try again.');
      setShowErrorModal(true);
    } finally {
      setRemovingIgloos(prev => {
        const newSet = new Set(prev);
        newSet.delete(iglooID);
        return newSet;
      });
    }
  };

  const loadPendingIglooInvites = async () => {
    const invitesList = [];
    const invitesCollectionRef = collection(
      db,
      "iglooInviteMessage",
      familyID,
      "iglooIDs"
    );
    try {
      const snapshot = await getDocs(invitesCollectionRef);
      for (let docSnap of snapshot.docs) {
        const iglooID = docSnap.id;
        const pendingRef = doc(db, "iglooInfo", iglooID, "pending", familyID);
        const pendingDocSnap = await getDoc(pendingRef);
        const fromFamilyID = pendingDocSnap.data()?.from;

        if (fromFamilyID) {
          const familyName = await getFamilyDisplayName(fromFamilyID);
          invitesList.push({ iglooID, familyName });
        }
      }
      setInvites(invitesList);
    } catch (error) {
      console.error("Error loading pending igloo invites:", error);
    }
  };

  const getFamilyDisplayName = async (targetFamilyID) => {
    if (familyNames[targetFamilyID]) {
      return familyNames[targetFamilyID];
    }

    let connectedFamilyRef = doc(
      db,
      "familyInfo",
      familyID,
      "connectedFamilies",
      targetFamilyID
    );

    try {
      let docSnap = await getDoc(connectedFamilyRef);
      if (!docSnap.exists()) {
        connectedFamilyRef = doc(db, "familyInfo", targetFamilyID);
        docSnap = await getDoc(connectedFamilyRef);
      }

      if (docSnap.exists()) {
        const name = docSnap.data().name;
        setFamilyNames((prevNames) => ({
          ...prevNames,
          [targetFamilyID]: name,
        }));
        return name;
      } else {
        return "Unknown Family";
      }
    } catch (error) {
      console.error("Error getting family display name:", error);
      return "Unknown Family";
    }
  };

  const acceptIglooInvite = async (iglooID, familyName) => {
    try {
      // Remove from pending
      const pendingRef = doc(db, "iglooInfo", iglooID, "pending", familyID);
      await deleteDoc(pendingRef);

      const inviteMessageRef = doc(db, "iglooInviteMessage", familyID, "iglooIDs", iglooID);
      await deleteDoc(inviteMessageRef);

      const familyIgloosRef = doc(db, "familyInfo", familyID, "igloos", iglooID);
      const iglooName = `NewIglooFrom${familyName}`;
      await setDoc(familyIgloosRef, { name: iglooName });

      loadPendingIglooInvites();
      triggerReload();
    } catch (error) {
      console.error("Error accepting igloo invite:", error);
    }
  };

  const declineIglooInvite = async (iglooID) => {
    const iglooRef = doc(db, "iglooInfo", iglooID);
    const pendingRef = doc(db, "iglooInfo", iglooID, "pending", familyID);
    const inviteMessageRef = doc(db, "iglooInviteMessage", familyID, "iglooIDs", iglooID);

    try {
      unsubscribeIglooListeners(iglooID);
      await deleteDoc(pendingRef);
    } catch (error) {
      console.error("Error in deleting pending ref:", error);
      return;
    }

    try {
      await deleteDoc(inviteMessageRef);
    } catch (error) {
      console.error("Error in deleting invite message ref:", error);
      return;
    }

    try {
      const docSnap = await getDoc(iglooRef);
      if (docSnap.exists()) {
        const data = docSnap.data();
        const updatedMembers = (data.members || []).filter(id => id !== familyID);
        const updatedControllers = (data.controllers || []).filter(id => id !== familyID);

        await updateDoc(iglooRef, {
          members: updatedMembers,
          controllers: updatedControllers
        });
      }
    } catch (error) {
      console.error("Error in updating igloo ref:", error);
      return;
    }

    loadPendingIglooInvites();
    resubscribeAllEnabled();
  };

  const triggerReload = () => {
    setReloadTrigger(prev => prev + 1);
    setTimeout(() => {
      if (igloos.length > 0) {
        loadMemberCounts();
      }
    }, 1000);
  };

  const toggleIglooPosition = async (iglooID, newPosition) => {
    const iglooRef = doc(db, "familyInfo", familyID, "igloos", iglooID);
    try {
      // Update the position in Firestore
      await updateDoc(iglooRef, { position: newPosition === "Inactive" ? deleteField() : newPosition });
      loadIgloos(); // Reload igloos to update the UI
    } catch (error) {
      console.error("Error updating igloo position:", error);
    }
  };

  const getIglooByPosition = (position) => {
    return igloos.find(igloo => igloo.position === position);
  };

  const getAvailableIgloos = (currentPosition) => {
    const currentIgloo = getIglooByPosition(currentPosition);
    const otherActiveIgloos = igloos.filter(igloo => 
      igloo.position !== "Inactive" && igloo.position !== currentPosition
    );
    const inactiveIgloos = igloos.filter(igloo => igloo.position === "Inactive");
    
    return [
      ...(currentIgloo ? [currentIgloo] : []),
      ...inactiveIgloos
    ];
  };

  const getMemberCount = (iglooID) => {
    return memberCounts[iglooID] || 0;
  };

  const loadMemberCounts = async () => {
    const counts = {};
    for (const igloo of igloos) {
      try {
        const iglooRef = doc(db, "iglooInfo", igloo.iglooID);
        const docSnap = await getDoc(iglooRef);
        
        if (docSnap.exists()) {
          const data = docSnap.data();
          const controllers = data.controllers || [];
          const members = data.members || [];
          const totalCount = controllers.length + members.length;
          counts[igloo.iglooID] = totalCount;
        } else {
          counts[igloo.iglooID] = 0;
          console.error(`Igloo ${igloo.iglooID}: 0 members (document doesn't exist)`);
        }
      } catch (error) {
        console.error(`Error loading member count for igloo ${igloo.iglooID}:`, error);
        counts[igloo.iglooID] = 0;
      }
    }
    
    setMemberCounts(counts);
  };

  const renderPositionCard = (position, colorClass, dotColor) => {
    const assignedIgloo = getIglooByPosition(position);
    const availableIgloos = getAvailableIgloos(position);

    return (
      <Card key={position} className="border-2 border-dashed border-slate-200">
        <CardHeader className="pb-4 !p-3 sm:!p-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="w-12 h-7 border-2 border-slate-300 rounded-md bg-slate-50 flex items-center justify-center relative">
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full border ${position === 'Left' ? `bg-blue-500 border-blue-500` : 'bg-slate-200 border-slate-300'}`}></div>
                  <div className={`w-2 h-2 rounded-full border ${position === 'Centre' ? `bg-green-500 border-green-500` : 'bg-slate-200 border-slate-300'}`}></div>
                  <div className={`w-2 h-2 rounded-full border ${position === 'Right' ? `bg-orange-500 border-orange-500` : 'bg-slate-200 border-slate-300'}`}></div>
                </div>
                <div className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-2 h-1 bg-slate-300 rounded-b-sm"></div>
              </div>
              <div>
                <div className="text-slate-700 uppercase tracking-wide font-light text-sm">{position.toUpperCase()} POSITION</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Select
                value={assignedIgloo?.iglooID || "none"}
                onValueChange={async (value) => {
                  if (value === "none") {
                    // Move current igloo to inactive
                    if (assignedIgloo) {
                      await toggleIglooPosition(assignedIgloo.iglooID, "Inactive");
                    }
                  } else {
                    // First move current igloo to inactive (if any), then assign new igloo
                    if (assignedIgloo && assignedIgloo.iglooID !== value) {
                      await toggleIglooPosition(assignedIgloo.iglooID, "Inactive");
                    }
                    await toggleIglooPosition(value, position);
                  }
                }}
              >
                <SelectTrigger className="w-full sm:w-48">
                  <SelectValue placeholder="No igloo assigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Higlo assigned</SelectItem>
                  {availableIgloos.map(igloo => (
                    <SelectItem key={igloo.iglooID} value={igloo.iglooID}>
                      {igloo.iglooName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>

        {assignedIgloo && (
          <CardContent className="pt-0 space-y-4 !p-2 sm:!p-4">
            {/* Igloo Configuration Section */}
            <div className="border rounded-lg bg-white shadow-sm">
              <div className="p-4 border-b bg-slate-50 rounded-t-lg">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center ${colorClass} flex-shrink-0`}>
                      <Users className="h-4 w-4 text-slate-900" />
                    </div>
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0 flex-1">
                      {editingIglooID === assignedIgloo.iglooID ? (
                        <div className="flex items-center gap-2 w-full">
                          <Input
                            type="text"
                            value={iglooName}
                            onChange={handleChange}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") handleSave(assignedIgloo.iglooID);
                              if (e.key === "Escape") handleCancel();
                            }}
                            className="font-semibold flex-1"
                            autoFocus
                          />
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleSave(assignedIgloo.iglooID)}
                            aria-label="Save Igloo Name"
                          >
                            <Check className="h-4 w-4 text-green-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon" 
                            onClick={handleCancel} 
                            aria-label="Cancel Edit"
                          >
                            <X className="h-4 w-4 text-red-600" />
                          </Button>
                        </div>
                      ) : (
                        <span 
                          className="font-semibold cursor-pointer hover:underline truncate"
                          onClick={() => handleEditToggle(assignedIgloo.iglooID, assignedIgloo.iglooName)}
                        >
                          {assignedIgloo.iglooName}
                        </span>
                      )}
                      <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-700 flex-shrink-0 self-start sm:self-auto">
                        {getMemberCount(assignedIgloo.iglooID)} members
                      </Badge>
                    </div>
                  </div>
                  <Button
                    onClick={() => toggleIglooConfig(`${position.toLowerCase()}-${assignedIgloo.iglooID}`)}
                    variant="default"
                    size="sm"
                    className="bg-black text-white hover:bg-black/90 w-full sm:w-auto"
                  >
                    {configuringIgloos[`${position.toLowerCase()}-${assignedIgloo.iglooID}`] ? "Hide Details" : "Configure"}
                  </Button>
                </div>
              </div>

              {configuringIgloos[`${position.toLowerCase()}-${assignedIgloo.iglooID}`] && (
                <div className="p-4 space-y-3">
                  <IglooMembers
                    iglooID={assignedIgloo.iglooID}
                    familyID={familyID}
                    isInviteView={false}
                    onUpdate={triggerReload}
                    {...getIglooLiveProps(assignedIgloo.iglooID)}
                  />
                </div>
              )}
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <Card>
      <CardHeader className="p-3">
        <div className="flex items-start gap-2">
          <Home className="h-5 w-5 text-slate-900 flex-shrink-0 mt-0.5" />
          <CardTitle className="leading-none">Higlos</CardTitle>
        </div>
        <CardDescription>Configure your Higlos and member positions</CardDescription>
      </CardHeader>

      <CardContent className="space-y-6 !p-2 sm:!p-4">
        {/* Auto Mode Banner */}
        {autoMode && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2">
            <Zap className="h-4 w-4 text-amber-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-amber-800">
              <span className="font-medium">Auto Mode is enabled.</span> Only the Centre position is available. The device will automatically connect to the centre Higlo when opened.
            </div>
          </div>
        )}

        {/* Position Layout */}
        <div className="space-y-4">
          {!autoMode && renderPositionCard("Left", "bg-blue-100", "bg-blue-500")}
          {renderPositionCard("Centre", "bg-green-100", "bg-green-500")}
          {!autoMode && renderPositionCard("Right", "bg-orange-100", "bg-orange-500")}
        </div>

        <Separator />

        {/* Inactive Igloos */}
        <div className="flex items-center justify-between">
          <Button
            onClick={() => toggleSection("inactive")}
            variant="ghost"
            className="p-0 h-auto font-semibold text-slate-900 hover:bg-transparent"
          >
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center">
                <Home className="h-3 w-3 text-slate-900" />
              </div>
              Inactive Higlos
              <Badge variant="secondary" className="ml-2 text-xs bg-slate-100 text-slate-700">
                {igloos.filter(igloo => igloo.position === "Inactive" || !igloo.position).length}
              </Badge>
              <ChevronDown
                className={`h-4 w-4 transition-transform duration-300 ${
                  expandedSections.inactive ? 'rotate-180' : ''
                }`}
              />
            </div>
          </Button>
        </div>

        {expandedSections.inactive && (
          <div className="space-y-3">
            {igloos.filter(igloo => igloo.position === "Inactive" || !igloo.position).map((igloo) => (
              <Card key={igloo.iglooID}>
                <CardContent className="p-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0 flex-1">
                      <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center flex-shrink-0">
                        <Users className="h-3 w-3 text-slate-600" />
                      </div>
                      <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-2 min-w-0 flex-1">
                        {editingIglooID === igloo.iglooID ? (
                          <div className="flex items-center gap-2 w-full">
                            <Input
                              type="text"
                              value={iglooName}
                              onChange={handleChange}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") handleSave(igloo.iglooID);
                                if (e.key === "Escape") handleCancel();
                              }}
                              className="font-medium text-sm flex-1"
                              autoFocus
                            />
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleSave(igloo.iglooID)}
                              aria-label="Save Igloo Name"
                            >
                              <Check className="h-4 w-4 text-green-600" />
                            </Button>
                            <Button 
                              variant="ghost" 
                              size="icon" 
                              onClick={handleCancel} 
                              aria-label="Cancel Edit"
                            >
                              <X className="h-4 w-4 text-red-600" />
                            </Button>
                          </div>
                        ) : (
                          <span 
                            className="font-medium cursor-pointer hover:underline truncate"
                            onClick={() => handleEditToggle(igloo.iglooID, igloo.iglooName)}
                          >
                            {igloo.iglooName}
                          </span>
                        )}
                        <Badge variant="secondary" className="text-xs bg-slate-100 text-slate-700 flex-shrink-0 self-start sm:self-auto">
                          {getMemberCount(igloo.iglooID)} members
                        </Badge>
                      </div>
                    </div>
                    <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                      <Select
                        value="Inactive"
                        onValueChange={async (value) => {
                          if (value !== "Inactive") {
                            // Check if the target position already has an igloo assigned
                            const currentlyAssignedIgloo = getIglooByPosition(value);
                            if (currentlyAssignedIgloo) {
                              // Move the currently assigned igloo to inactive first
                              await toggleIglooPosition(currentlyAssignedIgloo.iglooID, "Inactive");
                            }
                            // Then move this igloo to the target position
                            await toggleIglooPosition(igloo.iglooID, value);
                          }
                        }}
                      >
                        <SelectTrigger className="w-full sm:w-[180px] text-sm">
                          <SelectValue placeholder="Assign to Position" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Inactive">Assign to Position</SelectItem>
                          <SelectItem value="Left" disabled={igloos.some((i) => i.position === "Left")}>Left</SelectItem>
                          <SelectItem value="Centre" disabled={igloos.some((i) => i.position === "Centre")}>Centre</SelectItem>
                          <SelectItem value="Right" disabled={igloos.some((i) => i.position === "Right")}>Right</SelectItem>
                        </SelectContent>
                      </Select>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => toggleIglooConfig(`inactive-${igloo.iglooID}`)}
                          variant="default"
                          size="sm"
                          className="bg-black text-white hover:bg-black/90 flex-1 sm:flex-none"
                        >
                          {configuringIgloos[`inactive-${igloo.iglooID}`] ? "Hide Details" : "Configure"}
                        </Button>
                        <Button
                          onClick={() => handleRemoveIgloo(igloo.iglooID)}
                          disabled={!igloo.isController || removingIgloos.has(igloo.iglooID)}
                          variant="destructive"
                          size="sm"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  {configuringIgloos[`inactive-${igloo.iglooID}`] && (
                    <div className="mt-4 pt-4 border-t">
                      <IglooMembers
                        iglooID={igloo.iglooID}
                        familyID={familyID}
                        isInviteView={false}
                        onUpdate={triggerReload}
                        {...getIglooLiveProps(igloo.iglooID)}
                      />
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Invites Section */}
        {invites.length > 0 && (
          <>
            <Separator />
            <div>
              <h4 className="font-semibold text-slate-900 mb-4">Pending Invites</h4>
              <div className="space-y-4">
                {invites.map((invite) => (
                  <Card key={invite.iglooID}>
                    <CardContent className="p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="font-medium text-slate-900">Higlo invite from {invite.familyName}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            onClick={() => acceptIglooInvite(invite.iglooID, invite.familyName)}
                            variant="default"
                            size="sm"
                            className="bg-black text-white hover:bg-black/90 flex-1 sm:flex-none"
                          >
                            Accept
                          </Button>
                          <Button
                            onClick={() => declineIglooInvite(invite.iglooID)}
                            variant="destructive"
                            size="sm"
                            className="flex-1 sm:flex-none"
                          >
                            Decline
                          </Button>
                        </div>
                      </div>
                      <div className="mt-4">
                        <IglooMembers
                          iglooID={invite.iglooID}
                          familyID={familyID}
                          isInviteView={true}
                          {...getIglooLiveProps(invite.iglooID)}
                        />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </>
        )}

        <Separator />

        {/* Create New Igloo */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Input
            type="text"
            placeholder="Enter new Higlo name"
            value={newIglooName}
            onChange={(e) => setNewIglooName(e.target.value)}
            disabled={creatingIgloo}
            className="flex-1"
          />
          <Button
            onClick={createNewIgloo}
            disabled={creatingIgloo}
            variant="outline"
            className="flex items-center gap-2 w-full sm:w-auto"
          >
            <Plus className="h-4 w-4" />
            {creatingIgloo ? "Creating..." : "Create New Higlo"}
          </Button>
        </div>
      </CardContent>

      {/* Error Modal */}
      <ErrorModal
        isOpen={showErrorModal}
        message={errorMessage}
        onClose={() => {
          setShowErrorModal(false);
          setErrorMessage("");
          if (resetEditOnClose && editingIglooID) {
            const current = igloos.find(i => i.iglooID === editingIglooID);
            if (current) setIglooName(current.iglooName || "");
            setEditingIglooID(null);
            setResetEditOnClose(false);
          }
        }}
      />
    </Card>
  );
}

export default Igloos;
